import type { BrowseWarning } from '../api.ts';
import type { MapPoint, MapPolygon, MapRegion, WarningTier } from '../map-style.ts';

export type { MapRegion };

/**
 * How the dashboard draws the agencies' warnings — by the phone's rules
 * (`mobile/ios/EVAC/Screens/HomeView.swift`: `warningRings`, `chipCoordinate`,
 * `cluster`, `roadWarningMaxSpan`, `WarningPin`), so the two maps agree about
 * where a warning is and how much of the map it claims:
 *
 *   - each warning draws its own shapes: the document's real polygons when it
 *     carried any, else one box per district it names, else its union box;
 *   - a road closure draws NO area (a padded line is not an area anybody is
 *     in) and appears only once the map is at suburb scale;
 *   - every warning is ONE icon pin in a tier disc, on the centre of its
 *     largest shape — on a covered district, never in the empty space between
 *     districts the union box would put it in;
 *   - pins that would overlap on screen collapse into a counted badge.
 *
 * Pure, so the rules are tested without a map engine.
 */

export interface LatLon {
  latitude: number;
  longitude: number;
}

/**
 * Road closures appear only once the visible map is at most this many degrees
 * of latitude tall (~suburb scale) — the phone's `roadWarningMaxSpan`. Zoomed
 * out over a city they are hundreds of pins hiding the warnings that matter.
 */
export const ROAD_WARNING_MAX_SPAN = 0.35;

/**
 * Pins closer than this on screen share a counted badge. The phone's grid is
 * ~65pt for a 44pt touch target; the dashboard's pin is 28px under a mouse.
 */
export const WARNING_CELL_PX = 48;

/** The pin and area ids, so the tap handler can tell a warning from a building. */
export const WARNING_PIN_PREFIX = 'warning-pin:';
export const WARNING_CLUSTER_PREFIX = 'warning-cluster:';

/** CAP polygon strings are "lat,lon lat,lon …". */
function capRing(poly: string): LatLon[] {
  return poly
    .trim()
    .split(/\s+/)
    .map((pair) => pair.split(',').map(Number))
    .filter((p) => p.length === 2 && p.every(Number.isFinite))
    .map(([lat, lon]) => ({ latitude: lat as number, longitude: lon as number }));
}

function boxRing(b: {
  min_latitude: number;
  max_latitude: number;
  min_longitude: number;
  max_longitude: number;
}): LatLon[] {
  return [
    { latitude: b.min_latitude, longitude: b.min_longitude },
    { latitude: b.min_latitude, longitude: b.max_longitude },
    { latitude: b.max_latitude, longitude: b.max_longitude },
    { latitude: b.max_latitude, longitude: b.min_longitude },
  ];
}

/**
 * The shapes a warning covers. The agency's own polygons come first: a US
 * storm-based warning carries a precise polygon AND the coarse forecast zones
 * (UGC) it touches, and the zone boxes are far larger than the storm. Districts
 * are for the documents that name places instead of drawing them (the
 * Bureau's); the union box is the last resort.
 */
export function warningRings(w: BrowseWarning): LatLon[][] {
  if (w.cap_area_polygons && w.cap_area_polygons.length > 0) {
    const rings = w.cap_area_polygons.map(capRing).filter((r) => r.length >= 3);
    if (rings.length > 0) return rings;
  }
  if (w.areas && w.areas.length > 0) return w.areas.map(boxRing);
  return [boxRing(w)];
}

/**
 * Where the warning's one pin sits: the centre of its largest shape's box — on
 * a covered district, never between them (the union centre could land hundreds
 * of km from any of them).
 */
export function pinCoordinate(rings: LatLon[][]): LatLon | null {
  let best: { area: number; at: LatLon } | null = null;
  for (const ring of rings) {
    if (ring.length === 0) continue;
    const lats = ring.map((c) => c.latitude);
    const lons = ring.map((c) => c.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const area = (maxLat - minLat) * (maxLon - minLon);
    if (!best || area > best.area)
      best = { area, at: { latitude: (minLat + maxLat) / 2, longitude: (minLon + maxLon) / 2 } };
  }
  return best?.at ?? null;
}

/**
 * The tier a warning's severity earns: Extreme/Severe is `severe`, anything
 * milder (or unknown) `advice`. A closed road is disruption, not danger —
 * Victoria marks every closure Severe — so a road is always `advice`.
 */
export function warningTier(w: Pick<BrowseWarning, 'event_kind' | 'cap_severity'>): WarningTier {
  if (w.event_kind === 'road') return 'advice';
  return w.cap_severity === 'Extreme' || w.cap_severity === 'Severe' ? 'severe' : 'advice';
}

export interface DrawnWarning {
  id: string;
  warning: BrowseWarning;
  at: LatLon;
  tier: WarningTier;
}

export interface WarningCluster {
  id: string;
  at: LatLon;
  count: number;
  tier: WarningTier;
  warnings: BrowseWarning[];
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
}

export interface WarningLayout {
  singles: DrawnWarning[];
  clusters: WarningCluster[];
  polygons: MapPolygon[];
}

/**
 * Lay the warnings out for one settled viewport: which draw, as what, where.
 * `keepId` is the warning the operator picked in the strip — always drawn, and
 * never folded into a cluster, because showing where a warning is means
 * drawing it.
 */
export function layoutWarnings(
  warnings: BrowseWarning[],
  region: MapRegion,
  keepId: string | null = null,
): WarningLayout {
  const latSpan = Math.max(region.maxLatitude - region.minLatitude, 1e-6);
  const lonSpan = Math.max(region.maxLongitude - region.minLongitude, 1e-6);
  const showRoads = latSpan <= ROAD_WARNING_MAX_SPAN;
  const cellLat = Math.max((latSpan * WARNING_CELL_PX) / Math.max(region.heightPx, 1), 1e-6);
  const cellLon = Math.max((lonSpan * WARNING_CELL_PX) / Math.max(region.widthPx, 1), 1e-6);

  const polygons: MapPolygon[] = [];
  const kept: DrawnWarning[] = [];
  const cells = new Map<string, DrawnWarning[]>();
  for (const w of warnings) {
    const road = w.event_kind === 'road';
    if (road && !showRoads && w.id !== keepId) continue;
    const rings = warningRings(w);
    const at = pinCoordinate(rings);
    if (!at) continue;
    const tier = warningTier(w);
    if (!road)
      rings.forEach((ring, i) =>
        polygons.push({ id: `warning-${w.id}-${i}`, kind: 'warning', tier, ring }),
      );
    const drawn = { id: w.id, warning: w, at, tier };
    if (w.id === keepId) {
      kept.push(drawn);
      continue;
    }
    const key = `${Math.floor(at.longitude / cellLon)}:${Math.floor(at.latitude / cellLat)}`;
    const cell = cells.get(key);
    if (cell) cell.push(drawn);
    else cells.set(key, [drawn]);
  }

  const singles: DrawnWarning[] = [...kept];
  const clusters: WarningCluster[] = [];
  for (const members of cells.values()) {
    if (members.length === 1) {
      singles.push(members[0] as DrawnWarning);
      continue;
    }
    const lats = members.map((m) => m.at.latitude);
    const lons = members.map((m) => m.at.longitude);
    clusters.push({
      id: members
        .map((m) => m.id)
        .sort()
        .join(','),
      at: {
        latitude: lats.reduce((a, b) => a + b, 0) / lats.length,
        longitude: lons.reduce((a, b) => a + b, 0) / lons.length,
      },
      count: members.length,
      tier: members.some((m) => m.tier === 'severe') ? 'severe' : 'advice',
      warnings: members.map((m) => m.warning),
      minLatitude: Math.min(...lats),
      maxLatitude: Math.max(...lats),
      minLongitude: Math.min(...lons),
      maxLongitude: Math.max(...lons),
    });
  }
  return { singles, clusters, polygons };
}

/** The pins a layout puts on the map — ambient, so they never steer the camera. */
export function warningPoints(layout: WarningLayout): MapPoint[] {
  return [
    ...layout.singles.map<MapPoint>((d) => ({
      id: `${WARNING_PIN_PREFIX}${d.id}`,
      latitude: d.at.latitude,
      longitude: d.at.longitude,
      label: `${d.warning.source}: ${d.warning.headline}`,
      kind: 'warning',
      ambient: true,
      warning: { tier: d.tier, icon: d.warning.event_kind },
    })),
    ...layout.clusters.map<MapPoint>((c) => ({
      id: `${WARNING_CLUSTER_PREFIX}${c.id}`,
      latitude: c.at.latitude,
      longitude: c.at.longitude,
      label: `${c.count} warnings here — open to see them`,
      kind: 'warning',
      ambient: true,
      warning: { tier: c.tier, icon: 'other', count: c.count },
    })),
  ];
}
